<script setup>
import { Head, Link } from '@inertiajs/vue3';
import AppLogo from '@/components/AppLogo.vue';
import Icon from '@/components/Icon.vue';
import ScrollToTop from '@/components/ScrollToTop.vue';
import { ref, onMounted } from 'vue';
import { useAppearance } from '@/composables/useAppearance';

const props = defineProps({
    activeLocations: {
        type: Array,
        default: () => []
    }
});

// Theme management
const { appearance, updateAppearance } = useAppearance();

const toggleTheme = () => {
    updateAppearance(appearance.value === 'dark' ? 'light' : 'dark');
};

// Animation states
const isVisible = ref(false);
const counters = ref({
    pharmacies: 0,
    lots: 0,
    savings: 0
});

// Animate counters on mount
onMounted(() => {
    isVisible.value = true;

    // Animate stats counters
    const targets = { pharmacies: 150, lots: 2500, savings: 85 };
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    let step = 0;
    const timer = setInterval(() => {
        step++;
        const progress = step / steps;
        counters.value.pharmacies = Math.floor(targets.pharmacies * progress);
        counters.value.lots = Math.floor(targets.lots * progress);
        counters.value.savings = Math.floor(targets.savings * progress);

        if (step >= steps) clearInterval(timer);
    }, interval);
});

const testimonials = [
    {
        quote: "N'Comandi nous a permis de réduire nos pertes de 40% en 6 mois.",
        author: "Dr. Amina B.",
        role: "Pharmacienne, Alger"
    },
    {
        quote: "Une solution simple et efficace pour gérer nos surplus.",
        author: "Dr. Karim M.",
        role: "Pharmacien, Oran"
    },
    {
        quote: "L'interface est intuitive et le support est excellent.",
        author: "Dr. Sarah L.",
        role: "Pharmacienne, Constantine"
    }
];
</script>

<template>

    <Head title="Bienvenue sur N'Comandi" />

    <div class="min-h-screen bg-background text-foreground overflow-x-hidden">
        <!-- Navigation -->
        <header class="fixed inset-x-0 top-0 z-50 backdrop-blur-md bg-background/80 border-b border-border/50">
            <nav class="flex items-center justify-between p-4 lg:px-8 max-w-7xl mx-auto" aria-label="Global">
                <div class="flex lg:flex-1">
                    <AppLogo class="-m-1.5 p-1.5 h-8 w-auto" />
                </div>
                <div class="flex items-center gap-4">
                    <button @click="toggleTheme" type="button"
                        class="p-2 rounded-full text-foreground/80 hover:text-primary hover:bg-muted transition-colors"
                        aria-label="Toggle dark mode">
                        <Icon v-if="appearance !== 'dark'" name="Moon" class="h-5 w-5" />
                        <Icon v-else name="Sun" class="h-5 w-5" />
                    </button>
                    <Link v-if="$page.props.auth.user" :href="route('dashboard')"
                        class="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-lg hover:bg-primary/90 transition-all hover:scale-105">
                        Mon Espace Client
                    </Link>
                    <template v-else>
                        <Link :href="route('login')" class="text-sm font-semibold hover:text-primary transition-colors">
                            Se connecter
                        </Link>
                        <Link :href="route('register')"
                            class="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-lg hover:bg-primary/90 transition-all hover:scale-105">
                            Créer mon compte
                        </Link>
                    </template>
                </div>
            </nav>
        </header>

        <main>
            <!-- Hero Section -->
            <section class="relative min-h-screen flex flex-col items-center justify-center overflow-hidden pt-16">
                <!-- Animated Background -->
                <div class="absolute inset-0 -z-10">
                    <div class="absolute inset-0 bg-gradient-to-br from-emerald-500/5 via-background to-blue-500/5"></div>
                    <div class="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl animate-pulse">
                    </div>
                    <div class="absolute bottom-1/4 right-1/4 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl animate-pulse"
                        style="animation-delay: 1s;"></div>
                </div>

                <div
                    class="max-w-7xl mx-auto px-6 lg:px-8 py-10 w-full flex flex-col items-center justify-center mt-10">
                    <div class="text-center w-full max-w-4xl mx-auto flex flex-col items-center justify-center"
                        :class="{ 'opacity-100 translate-y-0': isVisible, 'opacity-0 translate-y-8': !isVisible }"
                        style="transition: all 0.8s ease-out;">

                        <!-- Badge Subtil Programme Pilote -->
                        <div
                            class="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-sm font-medium mb-8">
                            <span class="relative flex h-2 w-2">
                                <span
                                    class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                <span class="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                            </span>
                            🚀 Programme pilote local actuellement actif
                        </div>

                        <h1 class="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-tight max-w-3xl">
                            <span class="text-foreground">
                                Transformation digitale et
                            </span>
                            <br />
                            <span
                                class="bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent text-5xl sm:text-6xl lg:text-7xl inline-block mt-2 mb-2 font-black">
                                collaboration intelligente
                            </span>
                            <br />
                            <span class="text-foreground">entre officines.</span>
                        </h1>

                        <p class="mt-8 text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed font-normal">
                            Infrastructure digitale professionnelle dédiée à la visibilité, la coordination et l’optimisation de la mise en relation entre officines algériennes.
                        </p>

                        <div class="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
                            <Link :href="$page.props.auth.user ? route('dashboard') : route('register')"
                                class="group relative inline-flex items-center gap-2 rounded-full bg-emerald-600 px-8 py-4 text-base font-semibold text-white shadow-xl hover:shadow-emerald-500/20 transition-all hover:scale-105 hover:bg-emerald-700">
                                Accéder à l’espace professionnel
                                <Icon name="ArrowRight"
                                    class="h-5 w-5 transition-transform group-hover:translate-x-1" />
                            </Link>
                            <a href="#fonctionnement"
                                class="inline-flex items-center gap-2 rounded-full border border-border px-8 py-4 text-base font-semibold hover:bg-muted transition-all text-neutral-700 dark:text-neutral-300">
                                Découvrir le réseau
                            </a>
                        </div>

                    </div>
                </div>

                <!-- Scroll indicator -->
                <div class="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
                    <Icon name="ChevronDown" class="h-8 w-8 text-muted-foreground" />
                </div>
            </section>

            <!-- How It Works - Process Section -->
            <section id="fonctionnement" class="py-24 bg-muted/30">
                <div class="max-w-7xl mx-auto px-6 lg:px-8">
                    <div class="text-center max-w-3xl mx-auto mb-16">
                        <h2 class="text-sm font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Réseau Intelligent</h2>
                        <p class="mt-2 text-3xl sm:text-4xl font-black tracking-tight text-neutral-900 dark:text-white">Optimisez vos ressources et votre coordination en 3 étapes</p>
                        <p class="mt-4 text-lg text-muted-foreground">
                            Une solution SaaS moderne permettant de fluidifier et de sécuriser la synergie entre officines algériennes agréées.
                        </p>
                    </div>

                    <!-- 3-Step Process -->
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
                        <!-- Step 1 -->
                        <div class="relative group">
                            <div
                                class="p-8 bg-card rounded-2xl border border-border shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 h-full flex flex-col">
                                <img src="/images/illustrations/inventory.png" alt="Inventaire" width="690" height="542"
                                    class="w-full h-40 object-contain mb-6 transition-transform duration-300" />
                                <div class="flex items-center gap-4 mb-6">
                                    <div
                                        class="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                                        1
                                    </div>
                                    <h3 class="text-xl font-bold">1. Centralisez vos disponibilités</h3>
                                </div>
                                <p class="text-muted-foreground leading-relaxed text-sm">
                                    Organisez et gérez vos informations produits à travers une interface professionnelle moderne.
                                </p>
                                <div class="mt-auto pt-6 flex flex-wrap gap-2">
                                    <span
                                        class="text-xs px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-medium">Gestion de stock</span>
                                    <span
                                        class="text-xs px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-medium">Interface moderne</span>
                                    <span
                                        class="text-xs px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-medium">Simplicité</span>
                                </div>
                            </div>
                            <!-- Connector -->
                            <div
                                class="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-emerald-500 to-teal-500 z-10">
                            </div>
                        </div>
                        <!-- Step 2 -->
                        <div class="relative group">
                            <div
                                class="p-8 bg-card rounded-2xl border border-border shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 h-full flex flex-col">
                                <img src="/images/illustrations/match-center.png" alt="Trouvez & Négociez" width="637" height="581"
                                    class="w-full h-40 object-contain mb-6 transition-transform duration-300" />
                                <div class="flex items-center gap-4 mb-6">
                                    <div
                                        class="w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-500 to-blue-500 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                                        2
                                    </div>
                                    <h3 class="text-xl font-bold">2. Accédez à un réseau intelligent</h3>
                                </div>
                                <p class="text-muted-foreground leading-relaxed text-sm">
                                    Visualisez rapidement les opportunités et améliorez la réactivité entre officines.
                                </p>
                                <div class="mt-auto pt-6 flex flex-wrap gap-2">
                                    <span
                                        class="text-xs px-2.5 py-1 rounded-full bg-teal-500/10 text-teal-600 dark:text-teal-400 font-medium">Visualisation</span>
                                    <span
                                        class="text-xs px-2.5 py-1 rounded-full bg-teal-500/10 text-teal-600 dark:text-teal-400 font-medium">Alertes intelligentes</span>
                                    <span
                                        class="text-xs px-2.5 py-1 rounded-full bg-teal-500/10 text-teal-600 dark:text-teal-400 font-medium">Réseau</span>
                                </div>
                            </div>
                            <!-- Connector -->
                            <div
                                class="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-teal-500 to-blue-500 z-10">
                            </div>
                        </div>
                        <!-- Step 3 -->
                        <div class="group">
                            <div
                                class="p-8 bg-card rounded-2xl border border-border shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 h-full flex flex-col">
                                <img src="/images/illustrations/messaging.png" alt="Échangez en confiance" width="605" height="532"
                                    class="w-full h-40 object-contain mb-6 transition-transform duration-300" />
                                <div class="flex items-center gap-4 mb-6">
                                    <div
                                        class="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                                        3
                                    </div>
                                    <h3 class="text-xl font-bold">3. Collaborez en sécurité</h3>
                                </div>
                                <p class="text-muted-foreground leading-relaxed text-sm">
                                    Communiquez efficacement grâce à une infrastructure professionnelle fiable et sécurisée.
                                </p>
                                <div class="mt-auto pt-6 flex flex-wrap gap-2">
                                    <span
                                        class="text-xs px-2.5 py-1 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 font-medium">Sécurité</span>
                                    <span
                                        class="text-xs px-2.5 py-1 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 font-medium">Traçabilité</span>
                                    <span
                                        class="text-xs px-2.5 py-1 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 font-medium">Conformité</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Reserved Section - Confiance -->
            <section class="py-20 bg-card border-y border-border">
                <div class="max-w-5xl mx-auto text-center px-6 space-y-6">
                    <h3 class="text-2xl sm:text-3xl font-black text-neutral-900 dark:text-white">
                        Réservé exclusivement aux professionnels agréés
                    </h3>
                    <p class="text-muted-foreground max-w-2xl mx-auto leading-relaxed text-base">
                        N'Comandi garantit un espace hautement sécurisé et conforme. Chaque compte fait l'objet d'une vérification d'agrément rigoureuse pour assurer la fiabilité de notre infrastructure.
                    </p>
                    <div class="grid grid-cols-2 md:grid-cols-4 gap-4 pt-6 text-xs font-semibold text-neutral-700 dark:text-neutral-300">
                        <div class="p-4 bg-muted/40 rounded-2xl border border-border/50">🛡️ Vérification professionnelle</div>
                        <div class="p-4 bg-muted/40 rounded-2xl border border-border/50">🤝 Réseau sécurisé</div>
                        <div class="p-4 bg-muted/40 rounded-2xl border border-border/50">🔒 Données confidentielles</div>
                        <div class="p-4 bg-muted/40 rounded-2xl border border-border/50">⚙️ Infrastructure fiable</div>
                    </div>
                </div>
            </section>

            <!-- Social Proof: Active Wilayas & Communes -->
            <section v-if="activeLocations && activeLocations.length > 0" class="py-24 bg-muted/20 overflow-hidden relative">
                <!-- Background blur effects -->
                <div class="absolute inset-0 -z-10 overflow-hidden">
                    <div class="absolute -top-1/4 -right-1/4 w-96 h-96 bg-teal-500/5 rounded-full blur-3xl"></div>
                    <div class="absolute -bottom-1/4 -left-1/4 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl"></div>
                </div>

                <div class="max-w-7xl mx-auto px-6 lg:px-8 mb-12 text-center">
                    <h2 class="text-sm font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Présence Nationale</h2>
                    <p class="mt-2 text-3xl sm:text-4xl font-black tracking-tight text-neutral-900 dark:text-white">
                        Une communauté qui grandit dans toute l'Algérie
                    </p>
                    <p class="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
                        Découvrez les communes et wilayas où nos pharmaciens partenaires utilisent quotidiennement le réseau.
                    </p>
                </div>

                <!-- Horizontal Infinite Marquee -->
                <div class="space-y-6 max-w-full">
                    <!-- Row 1: Moving Left -->
                    <div class="relative flex overflow-x-hidden w-full mask-gradient">
                        <div class="animate-marquee flex gap-6 whitespace-nowrap py-4">
                            <div v-for="(loc, index) in activeLocations" :key="'r1-' + index"
                                class="inline-flex items-center gap-4 bg-card/60 backdrop-blur-md border border-border/85 px-6 py-4 rounded-2xl shadow-sm hover:shadow-md hover:border-emerald-500/30 transition-all duration-300 min-w-[290px]">
                                <div class="w-12 h-12 rounded-xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center font-black text-lg shadow-inner shrink-0">
                                    {{ loc.wilaya_code }}
                                </div>
                                <div class="flex flex-col text-left">
                                    <div class="flex items-center gap-2">
                                        <span class="font-bold text-sm text-foreground/90">{{ loc.author }}</span>
                                        <span class="relative flex h-2 w-2">
                                            <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                            <span class="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                                        </span>
                                    </div>
                                    <span class="text-xs text-muted-foreground font-semibold">{{ loc.commune }}</span>
                                    <span class="text-xs text-emerald-600 dark:text-emerald-400 font-bold uppercase tracking-wider mt-0.5">{{ loc.wilaya }}</span>
                                </div>
                            </div>
                        </div>
                        <!-- Duplicate for infinite effect -->
                        <div class="absolute top-0 animate-marquee2 flex gap-6 whitespace-nowrap py-4">
                            <div v-for="(loc, index) in activeLocations" :key="'r1-dup-' + index"
                                class="inline-flex items-center gap-4 bg-card/60 backdrop-blur-md border border-border/85 px-6 py-4 rounded-2xl shadow-sm hover:shadow-md hover:border-emerald-500/30 transition-all duration-300 min-w-[290px]">
                                <div class="w-12 h-12 rounded-xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center font-black text-lg shadow-inner shrink-0">
                                    {{ loc.wilaya_code }}
                                </div>
                                <div class="flex flex-col text-left">
                                    <div class="flex items-center gap-2">
                                        <span class="font-bold text-sm text-foreground/90">{{ loc.author }}</span>
                                        <span class="relative flex h-2 w-2">
                                            <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                            <span class="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                                        </span>
                                    </div>
                                    <span class="text-xs text-muted-foreground font-semibold">{{ loc.commune }}</span>
                                    <span class="text-xs text-emerald-600 dark:text-emerald-400 font-bold uppercase tracking-wider mt-0.5">{{ loc.wilaya }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Row 2: Moving Right -->
                    <div class="relative flex overflow-x-hidden w-full mask-gradient">
                        <div class="animate-marquee-reverse flex gap-6 whitespace-nowrap py-4">
                            <div v-for="(loc, index) in [...activeLocations].reverse()" :key="'r2-' + index"
                                class="inline-flex items-center gap-4 bg-card/60 backdrop-blur-md border border-border/85 px-6 py-4 rounded-2xl shadow-sm hover:shadow-md hover:border-emerald-500/30 transition-all duration-300 min-w-[290px]">
                                <div class="w-12 h-12 rounded-xl bg-teal-500/10 text-teal-600 flex items-center justify-center font-black text-lg shadow-inner shrink-0">
                                    {{ loc.wilaya_code }}
                                </div>
                                <div class="flex flex-col text-left">
                                    <div class="flex items-center gap-2">
                                        <span class="font-bold text-sm text-foreground/90">{{ loc.author }}</span>
                                        <span class="relative flex h-2 w-2">
                                            <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75"></span>
                                            <span class="relative inline-flex rounded-full h-2 w-2 bg-teal-500"></span>
                                        </span>
                                    </div>
                                    <span class="text-xs text-muted-foreground font-semibold">{{ loc.commune }}</span>
                                    <span class="text-xs text-teal-600 dark:text-teal-400 font-bold uppercase tracking-wider mt-0.5">{{ loc.wilaya }}</span>
                                </div>
                            </div>
                        </div>
                        <!-- Duplicate for infinite effect -->
                        <div class="absolute top-0 animate-marquee-reverse2 flex gap-6 whitespace-nowrap py-4">
                            <div v-for="(loc, index) in [...activeLocations].reverse()" :key="'r2-dup-' + index"
                                class="inline-flex items-center gap-4 bg-card/60 backdrop-blur-md border border-border/85 px-6 py-4 rounded-2xl shadow-sm hover:shadow-md hover:border-emerald-500/30 transition-all duration-300 min-w-[290px]">
                                <div class="w-12 h-12 rounded-xl bg-teal-500/10 text-teal-600 flex items-center justify-center font-black text-lg shadow-inner shrink-0">
                                    {{ loc.wilaya_code }}
                                </div>
                                <div class="flex flex-col text-left">
                                    <div class="flex items-center gap-2">
                                        <span class="font-bold text-sm text-foreground/90">{{ loc.author }}</span>
                                        <span class="relative flex h-2 w-2">
                                            <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75"></span>
                                            <span class="relative inline-flex rounded-full h-2 w-2 bg-teal-500"></span>
                                        </span>
                                    </div>
                                    <span class="text-xs text-muted-foreground font-semibold">{{ loc.commune }}</span>
                                    <span class="text-xs text-teal-600 dark:text-teal-400 font-bold uppercase tracking-wider mt-0.5">{{ loc.wilaya }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Section Programme Pilote -->
            <section class="py-20 bg-muted/10 relative overflow-hidden">
                <div class="max-w-4xl mx-auto text-center px-6 relative z-10 space-y-6">
                    <div class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 font-bold text-xs select-none">
                        🚀 Programme pilote local
                    </div>
                    <h3 class="text-3xl font-black text-neutral-900 dark:text-white">
                        Accompagner la transformation digitale
                    </h3>
                    <p class="text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
                        N'comandi accompagne actuellement les premières officines algériennes dans leur transformation digitale et l’optimisation de leur coordination professionnelle.
                    </p>
                </div>
            </section>

            <!-- CTA Section -->
            <section class="py-24 bg-gradient-to-br from-emerald-500/10 via-background to-teal-500/10 border-t border-border">
                <div class="max-w-4xl mx-auto px-6 lg:px-8 text-center space-y-8">
                    <h2 class="text-3xl sm:text-4xl font-black text-neutral-900 dark:text-white tracking-tight leading-tight">
                        Participez à la modernisation de la gestion officinale
                    </h2>
                    <p class="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                        Accédez à un réseau professionnel dédié à l’optimisation des stocks pharmaceutiques en Algérie.
                    </p>
                    <Link :href="$page.props.auth.user ? route('dashboard') : route('register')"
                        class="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-8 py-4 text-base font-bold text-white shadow-xl hover:shadow-emerald-500/20 hover:bg-emerald-700 transition-all hover:scale-105">
                        Accéder à l’espace professionnel
                        <Icon name="ArrowRight" class="h-5 w-5" />
                    </Link>
                </div>
            </section>
        </main>

        <!-- Footer -->
        <footer class="bg-card border-t border-border pt-16 pb-8">
            <div class="max-w-7xl mx-auto px-6 lg:px-8">
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
                    <!-- Brand -->
                    <div>
                        <div class="flex items-center gap-2 mb-4">
                            <AppLogo class="h-8 w-auto" />
                        </div>
                        <p class="text-muted-foreground text-sm leading-relaxed">
                            La première infrastructure digitale algérienne dédiée à la visibilité, la coordination et l’optimisation des flux et des stocks entre officines.
                        </p>
                        <div class="mt-6">
                            <h2 class="text-xs font-semibold uppercase tracking-wider mb-3 text-muted-foreground">Moyens de paiement</h2>
                            <div class="flex items-center gap-4">
                                <img src="/images/cib-edahabia.png" alt="CIB Edahabia" width="292" height="158" class="h-14 w-auto brightness-110 grayscale hover:grayscale-0 transition-all duration-300" />
                            </div>
                        </div>
                    </div>

                    <!-- Informations Légales -->
                    <div>
                        <h2 class="font-semibold text-sm uppercase tracking-wide mb-4">Informations Légales</h2>
                        <ul class="space-y-3 text-sm">
                            <li>
                                <Link :href="route('legal.terms')"
                                    class="text-muted-foreground hover:text-foreground transition-colors">
                                    Conditions d'utilisation
                                </Link>
                            </li>
                            <li>
                                <Link :href="route('legal.privacy')"
                                    class="text-muted-foreground hover:text-foreground transition-colors">
                                    Politique de confidentialité
                                </Link>
                            </li>
                            <li>
                                <Link :href="route('legal.after-sales')"
                                    class="text-muted-foreground hover:text-foreground transition-colors">
                                    Service après-vente
                                </Link>
                            </li>
                        </ul>
                    </div>

                    <!-- Liens Utiles -->
                    <div>
                        <h2 class="font-semibold text-sm uppercase tracking-wide mb-4">Liens Utiles</h2>
                        <ul class="space-y-3 text-sm">
                            <li>
                                <Link :href="route('legal.support')"
                                    class="text-muted-foreground hover:text-foreground transition-colors">
                                    Support Client & FAQ
                                </Link>
                            </li>
                            <li>
                                <Link :href="route('register')"
                                    class="text-muted-foreground hover:text-foreground transition-colors">
                                    Créer un compte
                                </Link>
                            </li>
                            <li>
                                <Link :href="route('login')"
                                    class="text-muted-foreground hover:text-foreground transition-colors">
                                    Se connecter
                                </Link>
                            </li>
                        </ul>
                    </div>

                    <!-- Contact -->
                    <div>
                        <h2 class="font-semibold text-sm uppercase tracking-wide mb-4">Contact</h2>
                        <ul class="space-y-3 text-sm text-muted-foreground">
                            <li class="flex items-center gap-2">
                                <Icon name="Chat" class="h-4 w-4 text-primary" />
                                <a href="mailto:contact@ncomandi.com"
                                    class="hover:text-foreground transition-colors">contact@ncomandi.com</a>
                            </li>
                            <li class="flex items-center gap-2">
                                <Icon name="ShieldCheck" class="h-4 w-4 text-primary" />
                                <a href="https://wa.me/213670089496?text=Bonjour%20je%20souhaite%20plus%20d'informations%20sur%20N'Comandi"
                                    target="_blank" class="hover:text-foreground transition-colors">+213 670 089 496</a>
                            </li>
                            <li class="flex items-center gap-2">
                                <Icon name="Package" class="h-4 w-4 text-primary" />
                                <span>Algérie</span>
                            </li>
                        </ul>
                        <!-- Social Icons -->
                        <div class="flex items-center gap-3 mt-4">
                            <!-- LinkedIn -->
                            <a href="https://www.linkedin.com/company/ncomandi" target="_blank" rel="noopener"
                                class="w-9 h-9 rounded-full bg-muted flex items-center justify-center hover:bg-[#0077B5] hover:text-white transition-all"
                                title="LinkedIn">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24"
                                    fill="currentColor">
                                    <path
                                        d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                                </svg>
                            </a>
                            <!-- Facebook -->
                            <a href="https://www.facebook.com/ncomandi" target="_blank" rel="noopener"
                                class="w-9 h-9 rounded-full bg-muted flex items-center justify-center hover:bg-[#1877F2] hover:text-white transition-all"
                                title="Facebook">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24"
                                    fill="currentColor">
                                    <path
                                        d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                                </svg>
                            </a>
                            <!-- Instagram -->
                            <a href="https://www.instagram.com/ncomandiapp" target="_blank" rel="noopener"
                                class="w-9 h-9 rounded-full bg-muted flex items-center justify-center hover:bg-gradient-to-tr hover:from-[#f09433] hover:via-[#e6683c] hover:to-[#bc1888] hover:text-white transition-all"
                                title="Instagram">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24"
                                    fill="currentColor">
                                    <path
                                        d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                                </svg>
                            </a>
                            <!-- WhatsApp -->
                            <a href="https://wa.me/213670089496?text=Bonjour%20je%20souhaite%20plus%20d'informations%20sur%20N'Comandi"
                                target="_blank" rel="noopener"
                                class="w-9 h-9 rounded-full bg-muted flex items-center justify-center hover:bg-[#25D366] hover:text-white transition-all"
                                title="WhatsApp">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24"
                                    fill="currentColor">
                                    <path
                                        d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 00-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                                </svg>
                            </a>
                        </div>
                    </div>
                </div>

                <!-- Bottom bar -->
                <div class="border-t border-border pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
                    <p class="text-muted-foreground text-sm">
                        © {{ new Date().getFullYear() }} N'Comandi. Tous droits réservés.
                    </p>
                    <div
                        class="flex flex-col md:flex-row items-center gap-2 md:gap-4 text-center md:text-right text-muted-foreground text-xs">


                        <p>Réseau 100% Algérien 🇩🇿</p>
                    </div>
                </div>
            </div>
        </footer>
        <ScrollToTop />
    </div>
</template>

<style scoped>
@keyframes float {

    0%,
    100% {
        transform: translateY(0);
    }

    50% {
        transform: translateY(-10px);
    }
}

.mask-gradient {
    mask-image: linear-gradient(to right, transparent, white 15%, white 85%, transparent);
    -webkit-mask-image: linear-gradient(to right, transparent, white 15%, white 85%, transparent);
}

.animate-marquee {
    animation: marquee 45s linear infinite;
}

.animate-marquee2 {
    animation: marquee2 45s linear infinite;
}

.animate-marquee-reverse {
    animation: marquee-reverse 45s linear infinite;
}

.animate-marquee-reverse2 {
    animation: marquee-reverse2 45s linear infinite;
}

@keyframes marquee {
    0% { transform: translateX(0%); }
    100% { transform: translateX(-100%); }
}

@keyframes marquee2 {
    0% { transform: translateX(100%); }
    100% { transform: translateX(0%); }
}

@keyframes marquee-reverse {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(0%); }
}

@keyframes marquee-reverse2 {
    0% { transform: translateX(0%); }
    100% { transform: translateX(100%); }
}
</style>